/**
 * Get a unique identifier from a row
 * @param {Object} row - The row object
 * @param {string|function} rowKey - The key to identify rows (can be a function)
 * @returns {string|number|null} The row ID
 */
export const getRowId = (row, rowKey) => {
  if (!row) {
    return null;
  }

  if (typeof rowKey === 'function') {
    return rowKey(row);
  }

  if (typeof rowKey === 'string' && row[rowKey] !== undefined) {
    return row[rowKey];
  }

  return row.id ?? row.key ?? null;
};

/**
 * Group rows by a key field and sum numeric fields across grouped rows.
 * @param {Array} rows - The data rows
 * @param {string} groupKey - The field to group by
 * @param {string[]} sumKeys - Numeric fields to sum across grouped rows
 * @returns {Array} Aggregated rows (one per unique groupKey value)
 */
export const groupRowsBy = (rows, groupKey, sumKeys) => {
  const map = new Map();

  for (const row of rows) {
    const key = row[groupKey];

    if (!map.has(key)) {
      map.set(key, { ...row });
    } else {
      const existing = map.get(key);

      for (const sumKey of sumKeys) {
        existing[sumKey] = (existing[sumKey] || 0) + (row[sumKey] || 0);
      }
    }
  }

  return Array.from(map.values());
};

/**
 * Format a numeric value as currency (USD)
 * @param {number|string|null} value - The value to format
 * @returns {string|null} Formatted currency string or null if invalid
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numVal = typeof value === 'number' ? value : parseFloat(value);

  if (Number.isNaN(numVal)) {
    return null;
  }

  return `$${numVal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/**
 * Parse a date string into a local Date object
 * Handles ISO date strings (YYYY-MM-DD format)
 * @param {string} dateString - The date string to parse
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) {
    return null;
  }

  const datePart = String(dateString).split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

/**
 * Format a Date object to API format (YYYY-MM-DD)
 * @param {Date} date - The date to format
 * @returns {string|null} Formatted date string or null if invalid
 */
export const formatDateForApi = (date) => {
  if (!date || !(date instanceof Date)) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
